/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { Suspense } from 'react';

import { jsx } from '@compiled/react';
import { defineMessages, type MessageDescriptor, useIntl } from 'react-intl';

import { IconButton } from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import ArrowRightIcon from '@atlaskit/icon/core/arrow-right';
import SlideIn from '@atlaskit/motion/slide-in';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { useSplitView } from '../split-layout/useSplitView';

import { SessionRightPanelLazy } from './SessionRightPanelLazy';
import { SessionRightPanelSkeleton } from './SessionRightPanelSkeleton';

type MessageKeys = 'closeContextPanelLabel' | 'contextPanelLabel';

const messages: Record<MessageKeys, MessageDescriptor> = defineMessages({
	contextPanelLabel: {
		id: 'ai-mate.conversation-assistant.session-context-panel-drawer.label-non-final',
		defaultMessage: 'Context panel',
		description:
			'Accessible name for the context panel dialog that shows sources and output from the conversation.',
	},
	closeContextPanelLabel: {
		id: 'ai-mate.conversation-assistant.session-context-panel-drawer.close-label-non-final',
		defaultMessage: 'Hide context',
		description:
			'Label for the button that closes the context panel drawer and returns the user to the chat.',
	},
});

const styles = cssMap({
	// Opaque cover shown instantly so the chat behind is never revealed while the content animates in.
	drawer: {
		position: 'absolute',
		top: token('space.0'),
		right: token('space.0'),
		bottom: token('space.0'),
		left: token('space.0'),
		zIndex: 1,
		display: 'flex',
		flexDirection: 'column',
		backgroundColor: token('elevation.surface.raised'),
		boxShadow: token('elevation.shadow.overlay'),
		borderRadius: 'inherit',
		overflowX: 'hidden',
		overflowY: 'hidden',
	},
	// Content animates in over the opaque cover (never over the chat).
	content: {
		display: 'flex',
		flexDirection: 'column',
		flexGrow: 1,
		minHeight: '0px',
	},
	toolbar: {
		display: 'flex',
		flexShrink: 0,
		alignItems: 'center',
		paddingTop: token('space.150'),
		paddingRight: token('space.150'),
		paddingBottom: token('space.150'),
		paddingLeft: token('space.150'),
	},
	body: {
		flexGrow: 1,
		minHeight: '0px',
	},
});

/**
 * Session context overlay used when there is not room for a side-by-side split (narrow surfaces, the
 * mini-modal, or when reopening after a split auto-collapses). `MainWrapper` mounts it only while
 * drawer presentation is active. The opaque cover mounts instantly and the content animates in via
 * `SlideIn`, so the chat is never revealed mid-animation.
 */
export const SessionContextPanelDrawer = (): JSX.Element => {
	const { formatMessage } = useIntl();
	const { closeSessionContext } = useSplitView();

	return (
		<Box
			xcss={styles.drawer}
			testId="conversation-assistant-session-context-panel-drawer"
			role="dialog"
			aria-label={formatMessage(messages.contextPanelLabel)}
		>
			<SlideIn enterFrom="right" fade="in" duration="medium">
				{({ className, style, ref }) => (
					// `className` passed explicitly (not spread) so Compiled merges it with the `css` prop.
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- required to forward the motion animation class from @atlaskit/motion's SlideIn render prop
					<div ref={ref} className={className} style={style} css={styles.content}>
						<Box xcss={styles.toolbar}>
							<IconButton
								testId="conversation-assistant-session-context-panel-drawer-close-button"
								appearance="subtle"
								isTooltipDisabled={false}
								label={formatMessage(messages.closeContextPanelLabel)}
								icon={ArrowRightIcon}
								onClick={closeSessionContext}
								autoFocus
							/>
						</Box>
						<Box xcss={styles.body}>
							<Suspense fallback={<SessionRightPanelSkeleton />}>
								<SessionRightPanelLazy />
							</Suspense>
						</Box>
					</div>
				)}
			</SlideIn>
		</Box>
	);
};
